import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Prop, State } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { IconName } from '../../shared/icons';
import { DEFAULT_WORKBENCH_THEME, WorkbenchTheme } from '../../shared/themes';
import { monkeyPatch } from '../../utils/monkey-patch';
import { NotificationVariant } from './notification.types';

const FALLBACK_REMOVE_TIMEOUT_MS = 250;
const NOTIFICATION_TOP_MARGIN = 8;
const IS_VISIBLE_CLASS = 'is-visible';

/**
 * @slot - content of notification
 * @slot footer - optional notification footer
 */
@Component({
  tag: 'wb-notification',
  styleUrl: 'notification.scss',
  shadow: true,
})
export class NotificationComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** visual variant to display */
  @Prop({ reflect: true })
  public variant?: NotificationVariant;

  /** the colour scheme */
  @Prop({ reflect: true })
  public theme: WorkbenchTheme = DEFAULT_WORKBENCH_THEME;

  /** custom icon */
  @Prop({ reflect: true })
  public icon?: IconName;

  /** If present, hides the dismiss button */
  @Prop({ reflect: true })
  public hideDismissButton: boolean = false;

  /** Aria label for the dismiss button */
  @Prop()
  public dismissButtonLabel: string = 'dismiss notification';

  /** lifetime in milliseconds, after which notification will be dismissed */
  @Prop({ reflect: true })
  public lifetime?: number;

  /** emits event, when dismiss button has been clicked */
  @Event()
  public wbdismiss!: EventEmitter;

  /** emits event, when the lifetime has been expired */
  @Event()
  public wbexpire!: EventEmitter;

  @State()
  public hasFooter: boolean = false;

  private TagNames = transformTagNames(this, 'wb-notification', ['wb-text', 'wb-icon', 'wb-round-button', 'wb-badge']);

  private lifeTimeTimeoutId: number;

  public connectedCallback(): void {
    monkeyPatch(this.el, 'remove', this.runDisappearAnimation);
  }

  public componentDidRender(): void {
    this.runAppearAnimation();

    if (this.lifetime) {
      this.constructLifeTimeExpiry();
    }
  }

  public disconnectedCallback(): void {
    if (this.lifeTimeTimeoutId) {
      window.clearTimeout(this.lifeTimeTimeoutId);
    }
  }

  private handleFooterSlotChange = (e: Event) => {
    this.hasFooter = Boolean((e.target as HTMLSlotElement).assignedElements().length);
  };

  private runAppearAnimation(): void {
    this.el.style.marginBottom = this.offsetHeight;
    requestAnimationFrame(() => this.showNotification());
  }

  private showNotification(): void {
    this.el.style.position = 'relative';
    this.el.style.marginBottom = 'initial';
    this.el.style.transition = 'all 0.2s cubic-bezier(0.3, 0, 0, 1)';
    this.el.classList.add(IS_VISIBLE_CLASS);
  }

  private runDisappearAnimation = (done: () => void) => {
    this.el.style.marginBottom = this.offsetHeight;
    this.el.classList.remove(IS_VISIBLE_CLASS);
    this.el.addEventListener('transitionend', () => done());
    setTimeout(() => done(), FALLBACK_REMOVE_TIMEOUT_MS); // fallback for missing transitionend event
  };

  private constructLifeTimeExpiry() {
    this.lifeTimeTimeoutId = window.setTimeout(() => {
      this.wbexpire.emit();
      this.el.remove();
    }, this.lifetime);
  }

  private get offsetHeight(): string {
    return `-${this.el.offsetHeight + NOTIFICATION_TOP_MARGIN}px`;
  }

  private getIconEl() {
    const { WbBadge, WbIcon } = this.TagNames;
    if (['success', 'warning', 'error'].includes(this.variant)) {
      return <WbBadge variant={this.variant}></WbBadge>;
    }

    if (this.icon) {
      return <WbIcon name={this.icon} class="icon" />;
    }

    return undefined;
  }

  public render(): JSX.Element {
    const { WbIcon, WbText, WbRoundButton } = this.TagNames;

    return (
      <Host role="status" class={{ 'has-footer': this.hasFooter, 'wb-notification': true }}>
        {this.getIconEl()}
        <WbText class="content" size="l" static>
          <slot />
        </WbText>
        <div class="footer">
          <slot name="footer" onSlotchange={this.handleFooterSlotChange} />
        </div>
        {!this.hideDismissButton && (
          <WbRoundButton size="s" class="dismiss-button" theme={this.theme === 'light' ? 'dark' : 'light'} elevation={this.theme === 'light' ? 's' : null} duration={this.lifetime} onClick={this.wbdismiss.emit}>
            <WbIcon name="bds/close/24" />
            {this.dismissButtonLabel}
          </WbRoundButton>
        )}
      </Host>
    );
  }
}
